import { useContext, useRef, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'

import {
  ListItemsContext,
  LoadingContext,
  useLoading
} from './list-items-context'

import {
  ListItemContainer,
  ContainerContent,
  ContainerImage,
  SkeletonMask
} from '../styled-components'

import LeftContainerItem from './list-items-left-container/left-container-item'
import CenterContainerItem from './list-items-center-container/center-container-item'

import { badgeColorOptions } from '../../../atoms/badges/constants'

import colorsOptions from '../../../fundamentals/colors-styles/constants'

import {
  listItemStateStyleVariantOptions,
  listItemListViewVariantOptions,
  listItemStateVariantOptions,
  listItemsCheckVariantOptions
} from '../constants'

import { progressBarVariantOptions } from '../../../atoms/progress-bar/constants'

import { DateTime } from 'luxon'

export const IsLoading = ({ children, isLoading, skeletonStyle }) => {
  const { loading } = useLoading()

  const load = isLoading ?? loading

  if (load) return <SkeletonMask skeletonStyle={skeletonStyle} />

  return children
}

IsLoading.propTypes = {
  children: PropTypes.node,
  isLoading: PropTypes.bool,
  skeletonStyle: PropTypes.object
}

const ListItemsItem = ({
  state,
  itemKey,
  leadingIcon,
  id,
  date,
  title,
  titleStyle,
  secondaryText,
  tertiaryText,
  badge,
  switchControl,
  metaData,
  textRightValue,
  progressBarControl,
  confirmIconIsShow,
  actionRight,
  image,
  loading = false,
  isCheck,
  ...props
}) => {
  const { listViewType, firstItemKey, lastItemKey, dividerFull } =
    useContext(ListItemsContext)

  state = state ?? listItemStateVariantOptions.DEFAULT

  const stateStyle = listItemStateStyleVariantOptions[state]

  const radiusImageSkeleton = () => {
    if (listViewType === listItemListViewVariantOptions.TOGETHER) {
      if (firstItemKey === itemKey) return '0px 16px 0px 0px'
      if (lastItemKey === itemKey) return '0px 0px 16px 0px'
      return '0px'
    }

    return '0px 16px 16px 0px'
  }

  const [heightContainer, setHeightContainer] = useState(0)
  const containerRef = useRef(null)

  useEffect(() => {
    if (containerRef.current) {
      setHeightContainer(containerRef.current.offsetHeight)
    }
  }, [])

  const memoizedValue = useMemo(() => ({ loading }), [loading])

  return (
    <LoadingContext.Provider value={memoizedValue}>
      <ListItemContainer
        ref={containerRef}
        stateStyle={stateStyle}
        listViewVariant={listViewType}
        firstItemKey={firstItemKey === itemKey}
        lastItemKey={lastItemKey === itemKey}
        inactive={
          state === listItemStateVariantOptions.INACTIVE ||
          state === listItemStateVariantOptions['READ-ONLY']
        }
        data-item={itemKey}
        {...props}
      >
        <ContainerContent
          withImage={!!image}
          listViewVariant={listViewType}
          dividerFull={dividerFull}
          isLastItem={lastItemKey === itemKey}
        >
          <LeftContainerItem
            itemKey={itemKey}
            leadingIcon={leadingIcon}
            state={state}
            stateStyle={stateStyle}
            loading={loading}
            isCheck={isCheck}
          />

          <CenterContainerItem
            state={state}
            itemKey={itemKey}
            id={id}
            date={date}
            title={title}
            titleStyle={titleStyle}
            secondaryText={secondaryText}
            tertiaryText={tertiaryText}
            badge={badge}
            switchControl={switchControl}
            metaData={metaData}
            textRightValue={textRightValue}
            progressBarControl={progressBarControl}
            confirmIconIsShow={confirmIconIsShow}
            actionRight={actionRight}
            stateStyle={stateStyle}
            loading={loading}
          />
        </ContainerContent>

        {image && (
          <IsLoading
            skeletonStyle={{
              width: '120px',
              height: `${heightContainer}px`,
              borderRadius: radiusImageSkeleton,
              borderBottom:
                listViewType === listItemListViewVariantOptions.TOGETHER
                  ? `1px solid ${colorsOptions.greys['grey-20']}`
                  : 'none'
            }}
          >
            <ContainerImage
              listViewVariant={listViewType}
              firstItemKey={firstItemKey === itemKey}
              lastItemKey={lastItemKey === itemKey}
              image={image}
              disable={state === listItemStateVariantOptions.INACTIVE}
            />
          </IsLoading>
        )}
      </ListItemContainer>
    </LoadingContext.Provider>
  )
}

ListItemsItem.propTypes = {
  state: PropTypes.oneOf(Object.values(listItemStateVariantOptions)),
  itemKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dividerFull: PropTypes.bool,
  checkComponent: PropTypes.shape({
    checkInputType: PropTypes.oneOf(
      Object.values(listItemsCheckVariantOptions)
    ),
    onChange: PropTypes.func,
    radioDefaultValue: PropTypes.string,
    radioCurrentSelection: PropTypes.string
  }),
  leadingIcon: PropTypes.node,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
  date: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.instanceOf(DateTime)
  ]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  secondaryText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  tertiaryText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  badge: PropTypes.shape({
    label: PropTypes.string,
    color: PropTypes.oneOf(Object.values(badgeColorOptions))
  }),
  switchControl: PropTypes.shape({
    isShow: PropTypes.bool,
    checked: PropTypes.bool,
    onChange: PropTypes.func
  }),
  metaData: PropTypes.shape({
    meta: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    metaRightBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
  }),
  progressBarControl: PropTypes.shape({
    value: PropTypes.number,
    variant: PropTypes.oneOf(Object.values(progressBarVariantOptions)),
    loading: PropTypes.bool,
    cancelButton: PropTypes.bool,
    onChange: PropTypes.func
  }),
  textRightValue: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  confirmIconIsShow: PropTypes.bool,
  actionRight: PropTypes.node,
  image: PropTypes.string,
  loading: PropTypes.bool,
  isCheck: PropTypes.bool
}

export default ListItemsItem
